import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Radar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';
import { useTheme } from '../../contexts/ThemeContext';
import { useFabricationStore } from '../../stores/fabricationStore';
import { migrateCapabilities, getCapabilityScore } from '../../utils/persona_engine_utils';
import { getDomainColor } from '../../utils/colorUtils';
import {
  CapabilityEntry,
  CapabilityDomain,
  CAPABILITY_DOMAINS,
  DOMAIN_COLORS,
} from '../../types/persona_engine';
import { Spinner } from '../common/Spinner';
import { FiPlus, FiTrash2, FiChevronDown, FiChevronUp, FiRefreshCw } from 'react-icons/fi';

ChartJS.register(RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

// ── Types ─────────────────────────────────────────────────────────────────────
type CapabilitiesView = 'grid' | 'table';
type SortKey = 'score_desc' | 'score_asc' | 'label_asc' | 'domain';
type DepthOption = 'brief' | 'standard' | 'deep';

const COUNT_OPTIONS = [8, 10, 12, 15, 20];
const DEPTH_OPTIONS: { value: DepthOption; label: string }[] = [
  { value: 'brief', label: 'Brief' },
  { value: 'standard', label: 'Standard' },
  { value: 'deep', label: 'Deep' },
];
const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: 'score_desc', label: 'Score ↓' },
  { value: 'score_asc', label: 'Score ↑' },
  { value: 'label_asc', label: 'Label A–Z' },
  { value: 'domain', label: 'Domain' },
];

// ── Domain badge ──────────────────────────────────────────────────────────────
const DomainBadge: React.FC<{ domain: CapabilityDomain | string }> = ({ domain }) => {
  const color = getDomainColor(domain);
  const label = domain.replace(/_/g, ' ');
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium text-white"
      style={{ backgroundColor: color }}
    >
      {label}
    </span>
  );
};

// ── Score ring (SVG circular progress) ───────────────────────────────────────
const ScoreRing: React.FC<{ score: number; domain: CapabilityDomain | string; size?: number }> = ({
  score,
  domain,
  size = 44,
}) => {
  const color = getDomainColor(domain);
  const r = (size - 6) / 2;
  const circ = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(10, score)) / 10;
  return (
    <svg width={size} height={size} className="flex-shrink-0">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#374151" strokeWidth={5} />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke={color}
        strokeWidth={5}
        strokeDasharray={circ}
        strokeDashoffset={circ * (1 - pct)}
        strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
      <text
        x={size / 2}
        y={size / 2 + 4}
        textAnchor="middle"
        fontSize={11}
        fontWeight="bold"
        fill={color}
      >
        {score}
      </text>
    </svg>
  );
};

// ── Evidence chip ─────────────────────────────────────────────────────────────
const EvidenceChip: React.FC<{ text: string; darkMode: boolean }> = ({ text, darkMode }) => {
  const truncated = text.length > 40 ? text.slice(0, 40) + '…' : text;
  return (
    <span
      title={text}
      className={`inline-block px-2 py-0.5 rounded text-xs border cursor-default ${
        darkMode
          ? 'bg-gray-600 border-gray-500 text-gray-300'
          : 'bg-gray-100 border-gray-200 text-gray-600'
      }`}
    >
      "{truncated}"
    </span>
  );
};

// ── Grid card ─────────────────────────────────────────────────────────────────
interface GridCardProps {
  entry: CapabilityEntry;
  darkMode: boolean;
  onScoreChange: (key: string, score: number) => void;
  onRemove: (key: string) => void;
}

const GridCard: React.FC<GridCardProps> = ({ entry, darkMode, onScoreChange, onRemove }) => {
  const [expanded, setExpanded] = useState(false);
  const color = getDomainColor(entry.domain);
  const isLegacy = !entry.report;

  return (
    <div
      className={`rounded-xl border overflow-hidden transition-shadow ${
        darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'
      } hover:shadow-md`}
    >
      {/* Card header — always visible */}
      <div
        className="flex items-start gap-3 p-4 cursor-pointer select-none"
        onClick={() => setExpanded(p => !p)}
      >
        <ScoreRing score={entry.score} domain={entry.domain} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <DomainBadge domain={entry.domain} />
          </div>
          <p className="text-sm font-semibold leading-tight">{entry.label}</p>
          {/* Mini progress bar */}
          <div className={`mt-2 h-1.5 rounded-full ${darkMode ? 'bg-gray-600' : 'bg-gray-200'}`}>
            <div
              className="h-1.5 rounded-full transition-all"
              style={{ width: `${(entry.score / 10) * 100}%`, backgroundColor: color }}
            />
          </div>
        </div>
        <button className="flex-shrink-0 mt-1 text-gray-400">
          {expanded ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />}
        </button>
      </div>

      {/* Expanded body */}
      {expanded && (
        <div className={`px-4 pb-4 border-t ${darkMode ? 'border-gray-600' : 'border-gray-100'}`}>
          {/* Report */}
          {entry.report ? (
            <div className="mt-3 space-y-2">
              {entry.report.split('\n').filter(Boolean).map((para, i) => (
                <p key={i} className={`text-xs leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  {para}
                </p>
              ))}
            </div>
          ) : (
            <p className={`mt-3 text-xs italic ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              No report — re-extract for full audit prose.
            </p>
          )}

          {/* Evidence */}
          {entry.evidence?.length > 0 && (
            <div className="mt-3">
              <p className={`text-xs font-semibold mb-1.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Evidence:
              </p>
              <div className="flex flex-wrap gap-1.5">
                {entry.evidence.map((e, i) => (
                  <EvidenceChip key={i} text={e} darkMode={darkMode} />
                ))}
              </div>
            </div>
          )}

          {/* Ceiling / Floor */}
          {(entry.ceiling || entry.floor) && (
            <div className="mt-3 space-y-1">
              {entry.ceiling && (
                <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  <span className="font-semibold text-green-500">↑ Ceiling:</span> {entry.ceiling}
                </p>
              )}
              {entry.floor && (
                <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  <span className="font-semibold text-red-400">↓ Floor:</span> {entry.floor}
                </p>
              )}
            </div>
          )}

          {/* Slider + remove */}
          <div className="mt-4 flex items-center gap-3">
            <input
              type="range"
              min={0}
              max={10}
              step={1}
              value={entry.score}
              onChange={e => onScoreChange(entry.key, parseInt(e.target.value, 10))}
              className="flex-1"
              style={{ accentColor: color }}
              onClick={e => e.stopPropagation()}
            />
            <span className={`text-xs tabular-nums w-8 text-right ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              {entry.score}/10
            </span>
            <button
              onClick={e => { e.stopPropagation(); onRemove(entry.key); }}
              className="p-1 rounded text-gray-400 hover:text-red-500 transition-colors"
              title="Remove"
            >
              <FiTrash2 size={13} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Table row ─────────────────────────────────────────────────────────────────
interface TableRowProps {
  entry: CapabilityEntry;
  darkMode: boolean;
  onScoreChange: (key: string, score: number) => void;
  onRemove: (key: string) => void;
}

const TableRow: React.FC<TableRowProps> = ({ entry, darkMode, onScoreChange, onRemove }) => {
  const [expanded, setExpanded] = useState(false);
  const color = getDomainColor(entry.domain);
  const firstSentence = entry.report
    ? entry.report.split(/[.!?]/)[0].trim() + '.'
    : '—';

  return (
    <>
      <tr
        className={`cursor-pointer transition-colors ${
          darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
        } ${expanded ? (darkMode ? 'bg-gray-700' : 'bg-gray-50') : ''}`}
        onClick={() => setExpanded(p => !p)}
      >
        {/* Domain */}
        <td className="px-3 py-2 whitespace-nowrap">
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
            <span className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              {entry.domain.replace(/_/g, ' ')}
            </span>
          </span>
        </td>
        {/* Label */}
        <td className="px-3 py-2">
          <div>
            <p className="text-sm font-medium">{entry.label}</p>
            <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{entry.key}</p>
          </div>
        </td>
        {/* Score */}
        <td className="px-3 py-2 whitespace-nowrap">
          <div className="flex items-center gap-2">
            <div className={`w-16 h-1.5 rounded-full ${darkMode ? 'bg-gray-600' : 'bg-gray-200'}`}>
              <div
                className="h-1.5 rounded-full"
                style={{ width: `${(entry.score / 10) * 100}%`, backgroundColor: color }}
              />
            </div>
            <span className="text-xs tabular-nums font-semibold">{entry.score}/10</span>
          </div>
        </td>
        {/* Report preview */}
        <td className={`px-3 py-2 max-w-[200px] text-xs truncate ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          {firstSentence}
        </td>
        {/* Ceiling */}
        <td className={`px-3 py-2 max-w-[160px] text-xs truncate ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          {entry.ceiling || '—'}
        </td>
        {/* Floor */}
        <td className={`px-3 py-2 max-w-[160px] text-xs truncate ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          {entry.floor || '—'}
        </td>
        {/* Actions */}
        <td className="px-3 py-2 whitespace-nowrap">
          <div className="flex items-center gap-1">
            <button className="text-gray-400 p-1 rounded hover:text-gray-200">
              {expanded ? <FiChevronUp size={13} /> : <FiChevronDown size={13} />}
            </button>
            <button
              onClick={e => { e.stopPropagation(); onRemove(entry.key); }}
              className="text-gray-400 p-1 rounded hover:text-red-500"
            >
              <FiTrash2 size={13} />
            </button>
          </div>
        </td>
      </tr>
      {expanded && (
        <tr className={darkMode ? 'bg-gray-750' : 'bg-gray-50'}>
          <td colSpan={7} className="px-4 py-3">
            <div className="space-y-2">
              {entry.report && (
                <p className={`text-xs leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  {entry.report}
                </p>
              )}
              {entry.evidence?.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {entry.evidence.map((e, i) => (
                    <EvidenceChip key={i} text={e} darkMode={darkMode} />
                  ))}
                </div>
              )}
              <div className="flex items-center gap-3 mt-2">
                <input
                  type="range"
                  min={0}
                  max={10}
                  step={1}
                  value={entry.score}
                  onChange={ev => onScoreChange(entry.key, parseInt(ev.target.value, 10))}
                  className="w-40"
                  style={{ accentColor: color }}
                />
                <span className="text-xs tabular-nums">{entry.score}/10</span>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

// ── Main panel ────────────────────────────────────────────────────────────────
const CapabilitiesPanel: React.FC = () => {
  const { darkMode } = useTheme();
  const {
    state,
    capabilitiesLoading,
    capabilitiesError,
    extractCapabilities,
    updateCapability,
    addCapability,
    removeCapability,
    currentPersonaId,
  } = useFabricationStore();

  // Controls bar state — persisted to localStorage
  const [view, setView] = useState<CapabilitiesView>(() =>
    (localStorage.getItem('caps_view') as CapabilitiesView) || 'grid'
  );
  const [count, setCount] = useState<number>(() =>
    parseInt(localStorage.getItem('caps_count') || '8', 10)
  );
  const [depth, setDepth] = useState<DepthOption>(() =>
    (localStorage.getItem('caps_depth') as DepthOption) || 'standard'
  );
  const [sort, setSort] = useState<SortKey>('score_desc');
  const [domainFilter, setDomainFilter] = useState<string>('all');
  const [showRadar, setShowRadar] = useState(true);

  // Add form
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState(5);
  const [showAddForm, setShowAddForm] = useState(false);

  // Table sort column
  const [tableSort, setTableSort] = useState<{ col: string; dir: 'asc' | 'desc' }>({
    col: 'score',
    dir: 'desc',
  });

  useEffect(() => { localStorage.setItem('caps_view', view); }, [view]);
  useEffect(() => { localStorage.setItem('caps_count', String(count)); }, [count]);
  useEffect(() => { localStorage.setItem('caps_depth', depth); }, [depth]);

  if (!state) return null;

  const hasPrompt = !!state.system_prompt;
  const rawCaps = state.capabilities || {};
  const capabilities = migrateCapabilities(rawCaps);
  const allEntries = Object.values(capabilities);
  const isLegacy = allEntries.length > 0 && allEntries.every(e => !e.report);

  // ── Probe awareness ───────────────────────────────────────────────────────
  const probes = state.probes ?? [];
  const totalProbes = probes.length;
  const testedProbes = probes.filter(
    p => p.last_passed !== null && p.last_passed !== undefined
  );
  const untestedProbes = probes.filter(
    p => p.last_passed === null || p.last_passed === undefined
  );
  const passedProbes = probes.filter(p => p.last_passed === true);
  const failedProbes = probes.filter(p => p.last_passed === false);
  const testedCount = testedProbes.length;
  const untestedCount = untestedProbes.length;

  // Confidence tiers:
  //   high   — ≥5 probes tested, ≥60% pass rate
  //   medium — 1-4 probes tested
  //   low    — no probes tested, scores are inference-only
  const confidenceTier: 'high' | 'medium' | 'low' =
    testedCount >= 5
      ? 'high'
      : testedCount >= 1
        ? 'medium'
        : 'low';

  const confidenceConfig = {
    high:   { label: 'High confidence', color: 'text-green-500',  bg: darkMode ? 'bg-green-900/30' : 'bg-green-50 border border-green-200',  icon: '✓' },
    medium: { label: 'Medium confidence', color: 'text-amber-400', bg: darkMode ? 'bg-amber-900/30' : 'bg-amber-50 border border-amber-200',  icon: '◑' },
    low:    { label: 'Inference only',    color: 'text-gray-400',  bg: darkMode ? 'bg-gray-700'     : 'bg-gray-50 border border-gray-200',    icon: '○' },
  }[confidenceTier];

  // ── Filtered + sorted entries ─────────────────────────────────────────────
  const filteredEntries = useMemo(() => {
    let entries = allEntries;
    if (domainFilter !== 'all') {
      entries = entries.filter(e => e.domain === domainFilter);
    }
    return [...entries].sort((a, b) => {
      switch (sort) {
        case 'score_desc': return b.score - a.score;
        case 'score_asc':  return a.score - b.score;
        case 'label_asc':  return a.label.localeCompare(b.label);
        case 'domain':     return a.domain.localeCompare(b.domain);
        default:           return 0;
      }
    });
  }, [allEntries, domainFilter, sort]);

  // ── Radar domain averages ─────────────────────────────────────────────────
  const radarData = useMemo(() => {
    const averages = CAPABILITY_DOMAINS.map(domain => {
      const entries = allEntries.filter(e => e.domain === domain);
      if (!entries.length) return 0;
      return entries.reduce((sum, e) => sum + e.score, 0) / entries.length;
    });
    return {
      labels: CAPABILITY_DOMAINS.map(d => d.replace(/_/g, ' ')),
      datasets: [
        {
          label: 'Domain Average',
          data: averages,
          backgroundColor: 'rgba(6, 182, 212, 0.15)',
          borderColor: 'rgba(6, 182, 212, 0.8)',
          borderWidth: 2,
          pointBackgroundColor: CAPABILITY_DOMAINS.map(d => DOMAIN_COLORS[d]),
          pointRadius: 4,
        },
      ],
    };
  }, [allEntries]);

  const radarOptions = {
    scales: {
      r: {
        min: 0,
        max: 10,
        ticks: { stepSize: 2, color: darkMode ? '#6b7280' : '#9ca3af', font: { size: 9 } },
        grid: { color: darkMode ? '#374151' : '#e5e7eb' },
        pointLabels: { color: darkMode ? '#d1d5db' : '#374151', font: { size: 10 } },
      },
    },
    plugins: { legend: { display: false } },
    maintainAspectRatio: true,
  };

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleExtract = () => extractCapabilities(count, depth);

  const handleScoreChange = (key: string, score: number) => {
    updateCapability(key, score);
  };

  const handleAdd = () => {
    const trimmed = newKey.trim().toLowerCase().replace(/\s+/g, '_');
    if (!trimmed) return;
    addCapability(trimmed, newValue);
    setNewKey('');
    setNewValue(5);
    setShowAddForm(false);
  };

  const handleTableSort = (col: string) => {
    setTableSort(prev =>
      prev.col === col
        ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { col, dir: 'desc' }
    );
  };

  const inputClass = `w-full p-2 rounded-md text-sm border ${
    darkMode
      ? 'bg-gray-700 text-white border-gray-600'
      : 'bg-white text-gray-800 border-gray-300'
  }`;

  const selectClass = `p-1.5 rounded-md text-xs border ${
    darkMode
      ? 'bg-gray-700 text-white border-gray-600'
      : 'bg-white text-gray-800 border-gray-300'
  }`;

  // ── Empty states ──────────────────────────────────────────────────────────
  const renderEmptyState = () => {
    if (!hasPrompt) {
      return (
        <div className={`mb-4 px-4 py-3 rounded-lg text-sm font-medium ${
          darkMode ? 'bg-amber-900/40 text-amber-300' : 'bg-amber-50 text-amber-700 border border-amber-200'
        }`}>
          Compile the artifact first — capabilities will be auto-extracted after compilation.
          You can also add them manually below.
        </div>
      );
    }
    if (allEntries.length === 0) {
      return (
        <div className={`mb-4 flex flex-col items-center justify-center py-12 text-center rounded-lg border border-dashed ${
          darkMode ? 'border-gray-600 text-gray-500' : 'border-gray-300 text-gray-400'
        }`}>
          <span className="text-3xl mb-3">⚡</span>
          <p className="text-sm font-medium mb-3">No capabilities extracted yet.</p>
          <button
            onClick={handleExtract}
            disabled={capabilitiesLoading}
            className="flex items-center gap-2 px-4 py-2 rounded-md bg-cyan-600 hover:bg-cyan-500 text-white text-sm font-medium"
          >
            {capabilitiesLoading ? <Spinner size="sm" /> : <FiRefreshCw size={14} />}
            Extract Capabilities
          </button>
        </div>
      );
    }
    return null;
  };

  return (
    <div>
      <h2 className="text-xl font-semibold mb-4">Capabilities</h2>

      {/* ── Error banner ──────────────────────────────────────────────────── */}
      {capabilitiesError && (
        <div className={`mb-4 px-4 py-2.5 rounded-lg text-sm ${
          darkMode ? 'bg-red-900/40 text-red-300' : 'bg-red-50 text-red-700 border border-red-200'
        }`}>
          {capabilitiesError}
        </div>
      )}

      {/* ── Legacy migration banner ───────────────────────────────────────── */}
      {isLegacy && (
        <div className={`mb-4 px-4 py-2.5 rounded-lg text-sm flex items-center gap-3 ${
          darkMode ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-50 text-blue-700 border border-blue-200'
        }`}>
          <span>These capabilities were imported from a legacy persona. Re-extract for full report cards.</span>
          <button
            onClick={handleExtract}
            disabled={capabilitiesLoading || !hasPrompt}
            className="ml-auto flex-shrink-0 px-3 py-1 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-xs font-medium"
          >
            {capabilitiesLoading ? <Spinner size="sm" /> : 'Re-extract'}
          </button>
        </div>
      )}

      {/* ── Probe awareness panel ────────────────────────────────────────── */}
      {hasPrompt && (
        <div className={`mb-4 rounded-lg p-3 text-sm ${confidenceConfig.bg}`}>
          <div className="flex items-center justify-between flex-wrap gap-2">
            {/* Confidence indicator */}
            <div className="flex items-center gap-2">
              <span className={`font-semibold ${confidenceConfig.color}`}>
                {confidenceConfig.icon} {confidenceConfig.label}
              </span>
              {totalProbes > 0 && (
                <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {testedCount}/{totalProbes} probes run
                  {failedProbes.length > 0 && (
                    <span className="ml-1 text-red-400">· {failedProbes.length} failed</span>
                  )}
                </span>
              )}
            </div>

            {/* Re-extract nudge when probes have been run */}
            {testedCount > 0 && allEntries.length > 0 && (
              <button
                onClick={handleExtract}
                disabled={capabilitiesLoading || !hasPrompt}
                className={`text-xs px-2 py-1 rounded-md font-medium transition-colors ${
                  darkMode
                    ? 'bg-cyan-800 hover:bg-cyan-700 text-cyan-200'
                    : 'bg-cyan-100 hover:bg-cyan-200 text-cyan-700'
                }`}
              >
                Re-extract with probe data
              </button>
            )}
          </div>

          {/* Guidance messages */}
          <div className={`mt-2 text-xs space-y-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            {confidenceTier === 'low' && totalProbes === 0 && (
              <p>
                ⚡ No probes defined. Scores are inferred from the system prompt only.{' '}
                <button
                  onClick={() => useFabricationStore.getState().setActivePanel(3)}
                  className={`underline font-medium ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}
                >
                  Generate probes →
                </button>
              </p>
            )}
            {confidenceTier === 'low' && totalProbes > 0 && untestedCount > 0 && (
              <p>
                ◑ {untestedCount} probe{untestedCount > 1 ? 's' : ''} defined but not yet run.
                Run them in the{' '}
                <button
                  onClick={() => useFabricationStore.getState().setActivePanel(3)}
                  className={`underline font-medium ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}
                >
                  Probes tab
                </button>{' '}
                to improve score accuracy.
              </p>
            )}
            {confidenceTier === 'medium' && untestedCount > 0 && (
              <p>
                ◑ {untestedCount} more probe{untestedCount > 1 ? 's' : ''} untested.
                Run them for higher confidence scores.{' '}
                <button
                  onClick={() => useFabricationStore.getState().setActivePanel(3)}
                  className={`underline font-medium ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}
                >
                  Go to Probes →
                </button>
              </p>
            )}
            {confidenceTier === 'medium' && testedCount < 5 && untestedCount === 0 && (
              <p>
                ◑ Only {testedCount} probe{testedCount > 1 ? 's' : ''} run. Generate more probes for higher confidence —
                aim for 5+ across different focus areas.{' '}
                <button
                  onClick={() => useFabricationStore.getState().setActivePanel(3)}
                  className={`underline font-medium ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}
                >
                  Generate more →
                </button>
              </p>
            )}
            {confidenceTier === 'high' && failedProbes.length > 0 && (
              <p>
                ✓ Good probe coverage.{' '}
                <span className="text-red-400 font-medium">{failedProbes.length} probe{failedProbes.length > 1 ? 's' : ''} failed</span>
                {' '}— scores in affected domains are adjusted downward. Review failures in the{' '}
                <button
                  onClick={() => useFabricationStore.getState().setActivePanel(3)}
                  className={`underline font-medium ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}
                >
                  Probes tab
                </button>.
              </p>
            )}
            {confidenceTier === 'high' && failedProbes.length === 0 && (
              <p>
                ✓ Strong probe coverage with all probes passing. Scores are empirically grounded.
              </p>
            )}
            {testedCount > 0 && allEntries.length === 0 && (
              <p>
                Probe results available — extract capabilities now to incorporate behavioral evidence.
              </p>
            )}
          </div>
        </div>
      )}

      {/* ── Controls bar ─────────────────────────────────────────────────── */}
      <div className={`flex flex-wrap items-center gap-2 mb-4 p-3 rounded-lg ${
        darkMode ? 'bg-gray-700' : 'bg-gray-50 border border-gray-200'
      }`}>
        {/* Extract button */}
        <button
          onClick={handleExtract}
          disabled={capabilitiesLoading || !hasPrompt}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium text-white transition-colors ${
            capabilitiesLoading || !hasPrompt
              ? 'bg-gray-400 cursor-not-allowed opacity-60'
              : 'bg-cyan-600 hover:bg-cyan-500'
          }`}
        >
          {capabilitiesLoading ? <Spinner size="sm" /> : <FiRefreshCw size={13} />}
          {allEntries.length > 0 ? 'Re-extract' : 'Extract'}
        </button>

        {/* Count */}
        <select
          value={count}
          onChange={e => setCount(parseInt(e.target.value, 10))}
          className={selectClass}
          title="Number of capabilities to extract"
        >
          {COUNT_OPTIONS.map(n => (
            <option key={n} value={n}>{n} caps</option>
          ))}
        </select>

        {/* Depth */}
        <select
          value={depth}
          onChange={e => setDepth(e.target.value as DepthOption)}
          className={selectClass}
          title="Report depth"
        >
          {DEPTH_OPTIONS.map(d => (
            <option key={d.value} value={d.value}>{d.label}</option>
          ))}
        </select>

        <div className={`w-px h-5 ${darkMode ? 'bg-gray-600' : 'bg-gray-300'}`} />

        {/* View toggle */}
        <div className={`flex rounded-md overflow-hidden border ${darkMode ? 'border-gray-600' : 'border-gray-300'}`}>
          {(['grid', 'table'] as CapabilitiesView[]).map(v => (
            <button
              key={v}
              onClick={() => setView(v)}
              className={`px-3 py-1.5 text-xs font-medium capitalize transition-colors ${
                view === v
                  ? 'bg-cyan-600 text-white'
                  : darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-white text-gray-600 hover:bg-gray-100'
              }`}
            >
              {v}
            </button>
          ))}
        </div>

        {/* Sort */}
        <select
          value={sort}
          onChange={e => setSort(e.target.value as SortKey)}
          className={selectClass}
        >
          {SORT_OPTIONS.map(s => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>

        {/* Domain filter */}
        <select
          value={domainFilter}
          onChange={e => setDomainFilter(e.target.value)}
          className={selectClass}
        >
          <option value="all">All domains</option>
          {CAPABILITY_DOMAINS.map(d => (
            <option key={d} value={d}>{d.replace(/_/g, ' ')}</option>
          ))}
        </select>

        {/* Radar toggle */}
        <button
          onClick={() => setShowRadar(p => !p)}
          className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
            showRadar
              ? darkMode ? 'bg-cyan-800 text-cyan-200' : 'bg-cyan-100 text-cyan-700'
              : darkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-600'
          }`}
        >
          {showRadar ? 'Hide Radar' : 'Show Radar'}
        </button>
      </div>

      {/* ── Empty states ─────────────────────────────────────────────────── */}
      {renderEmptyState()}

      {/* ── Radar chart ──────────────────────────────────────────────────── */}
      {showRadar && allEntries.length > 0 && (
        <div className={`mb-6 p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50 border border-gray-200'}`}>
          <p className={`text-xs font-semibold mb-3 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Domain Overview (average score per domain)
          </p>
          <div className="max-w-sm mx-auto">
            <Radar data={radarData} options={radarOptions as any} />
          </div>
        </div>
      )}

      {/* ── Grid view ────────────────────────────────────────────────────── */}
      {view === 'grid' && filteredEntries.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-4">
          {filteredEntries.map(entry => (
            <GridCard
              key={entry.key}
              entry={entry}
              darkMode={darkMode}
              onScoreChange={handleScoreChange}
              onRemove={removeCapability}
            />
          ))}
        </div>
      )}

      {/* ── Table view ───────────────────────────────────────────────────── */}
      {view === 'table' && filteredEntries.length > 0 && (
        <div className={`mb-4 rounded-lg overflow-hidden border ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
          <div className="overflow-x-auto">
            <table className={`w-full text-sm ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
              <thead>
                <tr className={darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-50 text-gray-600'}>
                  {[
                    { col: 'domain', label: 'Domain' },
                    { col: 'label', label: 'Capability' },
                    { col: 'score', label: 'Score' },
                    { col: 'report', label: 'Report' },
                    { col: 'ceiling', label: 'Ceiling' },
                    { col: 'floor', label: 'Floor' },
                    { col: 'actions', label: '' },
                  ].map(({ col, label }) => (
                    <th
                      key={col}
                      className={`px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide ${
                        col !== 'actions' && col !== 'report' ? 'cursor-pointer hover:text-cyan-400' : ''
                      }`}
                      onClick={() => col !== 'actions' && col !== 'report' && handleTableSort(col)}
                    >
                      {label}
                      {tableSort.col === col && (
                        <span className="ml-1">{tableSort.dir === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className={`divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-100'}`}>
                {filteredEntries.map(entry => (
                  <TableRow
                    key={entry.key}
                    entry={entry}
                    darkMode={darkMode}
                    onScoreChange={handleScoreChange}
                    onRemove={removeCapability}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Add capability form ───────────────────────────────────────────── */}
      {showAddForm ? (
        <div className={`rounded-lg p-4 border mb-4 ${
          darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
        }`}>
          <h4 className="font-medium text-sm mb-3">Add Capability</h4>
          <div className="space-y-2">
            <input
              className={inputClass}
              placeholder="Capability name (e.g. technical_depth)"
              value={newKey}
              onChange={e => setNewKey(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') handleAdd();
                if (e.key === 'Escape') setShowAddForm(false);
              }}
              autoFocus
            />
            <div className="flex items-center gap-3">
              <span className="text-sm flex-shrink-0">Value: {newValue}/10</span>
              <input
                type="range"
                min={0}
                max={10}
                step={1}
                value={newValue}
                onChange={e => setNewValue(parseInt(e.target.value, 10))}
                className="flex-1 accent-cyan-500"
              />
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            <button
              onClick={handleAdd}
              disabled={!newKey.trim()}
              className={`px-3 py-1.5 rounded-md text-sm font-medium text-white transition-colors ${
                newKey.trim() ? 'bg-cyan-600 hover:bg-cyan-500' : 'bg-gray-400 cursor-not-allowed'
              }`}
            >
              Add
            </button>
            <button
              onClick={() => setShowAddForm(false)}
              className={`px-3 py-1.5 rounded-md text-sm ${
                darkMode ? 'bg-gray-600 hover:bg-gray-500 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
              }`}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setShowAddForm(true)}
          className={`w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-dashed text-sm transition-colors ${
            darkMode
              ? 'border-gray-600 text-gray-400 hover:border-cyan-500 hover:text-cyan-400'
              : 'border-gray-300 text-gray-500 hover:border-cyan-400 hover:text-cyan-600'
          }`}
        >
          <FiPlus size={14} />
          Add Capability
        </button>
      )}
    </div>
  );
};

export default CapabilitiesPanel;
